import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Star, Wifi, WifiOff, Tag, X, Plus, Layers, Trash2, Activity, Network, Filter, ShieldCheck, ShieldAlert, ShieldOff, EyeOff, ScreenShare, Search, Moon } from 'lucide-react';
import { useTenantScope } from '../context/TenantScopeContext';
import ColumnPicker, { ColumnDef, DraggableTh, useColumnPrefs } from '../components/ColumnPicker';
import TenantSelect from '../components/TenantSelect';

interface AssetGroup {
  id: number;
  tenant_id: number;
  tenant_name: string;
  name: string;
  color: string | null;
  severity_boost: number;
  unreachable_minutes: number | null;
  monitor_reachability: boolean;
  members_count: number;
}

interface Asset {
  id: number;
  tenant_id: number;
  tenant_name: string;
  hostname: string;
  ip_address: string | null;
  mac_address: string | null;
  vendor: string | null;
  device_type: string | null;
  owner: string | null;
  notes: string | null;
  // Onboarding (2026-10-19): ruolo di business e autorizzazione a restare acceso di notte.
  business_role: string | null;
  night_allowed: boolean;
  group_id: number | null;
  group_name: string | null;
  group_color: string | null;
  active_check_ok: boolean | null;
  active_check_at: string | null;
  active_check_rtt_ms: number | null;
  check_tcp_port: number | null;
  active_check_tcp_ok: boolean | null;
  tags: string[];
  business_critical: boolean;
  first_seen_at: string;
  last_seen_at: string;
  silent_minutes: number | null;
  reachable: boolean;
  frequency: 'habitual' | 'occasional';
  risk_score: number;
  edr_vendors: string[] | null;
  edr_online: boolean | null;
  edr_isolated: boolean | null;
  edr_infected: boolean | null;
  edr_up_to_date: boolean | null;
  tenant_has_edr: boolean;
  // Stato di copertura calcolato dal backend (services/edrStatus.ts), lo stesso del job di
  // copertura e del report: la UI non lo ricalcola.
  edr_state: EdrState;
  // Da cosa deriva lo stato: 'edr' (riportato da un EDR), 'tag' (no-edr / edr-required),
  // 'oui' (produttore della scheda), 'hostname', 'os', 'none'.
  edr_state_reason: 'edr' | 'edr-no-antimalware' | 'tag' | 'oui' | 'hostname' | 'os' | 'none';
  // Tool di accesso remoto (2026-09-24): ultimo visto e policy; il default del tenant serve a
  // distinguere "senza policy in modalità informativa" (da smaltire) dal resto.
  remote_access_policy: 'inherit' | 'deny' | 'allow';
  remote_access_last_tool: string | null;
  remote_access_last_seen_at: string | null;
  tenant_remote_access_default: 'info' | 'deny' | 'allow';
}

// Asset su cui un tool remoto è stato visto, senza policy propria, con il tenant in modalità
// informativa: nessun incident finché qualcuno non decide — il contatore da smaltire.
function remotePending(a: Asset): boolean {
  return !!a.remote_access_last_seen_at && a.remote_access_policy === 'inherit' && a.tenant_remote_access_default === 'info';
}

const EDR_LABELS: Record<string, string> = {
  sentinelone: 'SentinelOne', crowdstrike: 'CrowdStrike', bitdefender: 'Bitdefender', acronis: 'Acronis', sangfor_xdr: 'Sangfor XDR', sangfor_epp: 'Sangfor EPP',
};

type EdrState = 'protected' | 'unprotected' | 'ineligible' | 'unclassified' | 'n/a';
type EdrFilter = 'all' | EdrState;

// Quattro stati (dal 2026-10-27): protetto (un EDR vivo lo riporta), non protetto (può avere un
// agent e non ce l'ha: il gap reale), non idoneo (stampanti, apparati di rete, telecamere: per
// OUI o tag "no-edr"), non identificato (segnaposto senza nome, OS e OUI noto). Calcolati dal
// backend con la stessa regola del controllo di copertura e del report.
function edrState(a: Asset): EdrState {
  return a.edr_state || 'n/a';
}

const EDR_REASON_LABELS: Record<Asset['edr_state_reason'], string> = {
  edr: 'reported by the EDR', 'edr-no-antimalware': 'the EDR reports it without the antimalware component (backup-only agent)',
  tag: 'set by an analyst tag', oui: 'from the network card vendor',
  hostname: 'it has a machine name', os: 'its operating system is known', none: 'no name, OS or known vendor',
};

const ASSET_COLUMNS: ColumnDef[] = [
  { key: 'mac', label: 'MAC Address', defaultVisible: true },
  { key: 'hostname', label: 'Hostname', defaultVisible: true },
  { key: 'ip', label: 'IP Address', defaultVisible: true },
  { key: 'vendor', label: 'Vendor', defaultVisible: true },
  { key: 'type', label: 'Device Type', defaultVisible: true },
  { key: 'edr', label: 'EDR', defaultVisible: true },
  { key: 'group', label: 'Group', defaultVisible: true },
  { key: 'owner', label: 'Owner', defaultVisible: true },
  { key: 'role', label: 'Business Role', defaultVisible: false },
  { key: 'night', label: 'Night-time', defaultVisible: false },
  { key: 'tcp_port', label: 'Service Port', defaultVisible: false },
  { key: 'notes', label: 'Notes', defaultVisible: false },
  { key: 'tags', label: 'Tags', defaultVisible: true },
  { key: 'detection', label: 'Detection', defaultVisible: true },
  { key: 'risk_score', label: 'Risk Score', defaultVisible: true },
  { key: 'status', label: 'Status', defaultVisible: true },
  { key: 'last_seen', label: 'Last Seen', defaultVisible: true },
  { key: 'first_seen', label: 'First Seen', defaultVisible: false },
  { key: 'critical', label: 'Business Critical', defaultVisible: true },
];

function formatAgo(minutes: number | null): string {
  if (minutes === null) return 'never';
  const value = Math.round(minutes);
  if (value < 1) return 'just now';
  if (value < 60) return `${value} min ago`;
  const hours = Math.floor(value / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function riskScoreStyle(score: number): string {
  if (score >= 70) return 'bg-red-100 text-red-700';
  if (score >= 40) return 'bg-amber-100 text-amber-700';
  if (score > 0) return 'bg-sky-50 text-sky-700';
  return 'bg-gray-100 text-gray-400';
}

const DEVICE_TYPE_LABELS: Record<string, string> = {
  firewall: 'Firewall', ips: 'IPS', endpoint: 'Endpoint', network: 'Network', server: 'Server', proxy: 'Proxy', dhcp: 'DHCP',
};

// Campo testuale modificabile inline (owner/note): salva su Enter o alla perdita di focus.
function EditableText({ value, placeholder, onSave }: { value: string | null; placeholder: string; onSave: (v: string) => void }) {
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => { setDraft(value ?? ''); }, [value]);
  const commit = () => { if (draft.trim() !== (value ?? '')) onSave(draft.trim()); };
  return (
    <input
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
      placeholder={placeholder}
      className="input-field py-0.5 px-1.5 text-xs w-28 placeholder:text-gray-300"
    />
  );
}

interface AnalysisExclusion {
  id: number;
  tenant_id: number;
  tenant_name: string;
  scope_type: 'asset' | 'cidr';
  asset_id: number | null;
  asset_hostname: string | null;
  cidr: string | null;
  reason: string;
  notes: string | null;
}

const EXCLUSION_REASONS: { value: string; label: string }[] = [
  { value: 'guest_network', label: 'Guest network' },
  { value: 'not_under_contract', label: 'Not under contract' },
  { value: 'not_relevant', label: 'Not relevant' },
  { value: 'customer_request', label: 'Customer request' },
];
const REASON_LABELS: Record<string, string> = Object.fromEntries(EXCLUSION_REASONS.map(r => [r.value, r.label]));

// Esclude un asset (per id, segue l'hostname anche se rinominato) o un intero range IP dalla
// detection e dall'inventario asset (ingestion/analysisExclusions.ts): niente case/alert, niente
// tracciamento come asset — i log grezzi restano comunque in Events Explorer. Pensato per reti
// guest, dispositivi non a contratto/ininfluenti o esclusi su richiesta del cliente.
function ExclusionsPanel({ exclusions, assets, tenantId, onChanged }: { exclusions: AnalysisExclusion[]; assets: Asset[]; tenantId: number | null; onChanged: () => void }) {
  const [scopeType, setScopeType] = useState<'asset' | 'cidr'>('cidr');
  const [assetId, setAssetId] = useState('');
  const [cidr, setCidr] = useState('');
  const [reason, setReason] = useState(EXCLUSION_REASONS[0].value);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const canCreate = tenantId && (scopeType === 'asset' ? !!assetId : !!cidr.trim());

  async function create() {
    if (!canCreate) return;
    setSaving(true);
    setError(null);
    try {
      await axios.post('/api/analysis-exclusions', {
        tenantId, scopeType, assetId: scopeType === 'asset' ? Number(assetId) : undefined,
        cidr: scopeType === 'cidr' ? cidr.trim() : undefined, reason, notes: notes.trim() || undefined,
      });
      setAssetId(''); setCidr(''); setNotes('');
      onChanged();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error while creating the exclusion');
    } finally {
      setSaving(false);
    }
  }

  async function remove(e: AnalysisExclusion) {
    const label = e.scope_type === 'asset' ? e.asset_hostname : e.cidr;
    if (!window.confirm(`Remove exclusion for "${label}"? Its logs will be analyzed and tracked again.`)) return;
    await axios.delete(`/api/analysis-exclusions/${e.id}`);
    onChanged();
  }

  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-2.5 border-b border-gray-100">
        <h2 className="font-medium text-sm text-gray-900 inline-flex items-center gap-2"><EyeOff size={15} /> Analysis Exclusions</h2>
        <p className="text-xs text-gray-400">
          Excluded assets/IP ranges never generate cases or alerts and are never tracked in the asset
          inventory — their raw logs are still collected (visible in Events Explorer), only analysis
          is skipped. Takes up to 30s to apply.
        </p>
      </div>
      {error && <div className="px-4 py-2 text-xs text-red-600 bg-red-50">{error}</div>}
      <table className="w-full text-sm">
        <thead><tr>
          <th className="table-header">Tenant</th><th className="table-header">Excludes</th>
          <th className="table-header">Reason</th><th className="table-header">Notes</th><th className="table-header"></th>
        </tr></thead>
        <tbody>
          {exclusions.map(e => (
            <tr key={e.id} className="border-t border-gray-100">
              <td className="table-cell text-gray-500">{e.tenant_name}</td>
              <td className="table-cell font-mono text-xs text-gray-700">{e.scope_type === 'asset' ? e.asset_hostname : e.cidr}</td>
              <td className="table-cell"><span className="badge bg-gray-100 text-gray-600">{REASON_LABELS[e.reason] || e.reason}</span></td>
              <td className="table-cell text-gray-500 text-xs">{e.notes || '—'}</td>
              <td className="table-cell">
                <button onClick={() => remove(e)} className="p-1 text-gray-400 hover:text-red-600" title="Remove exclusion">
                  <Trash2 size={14} />
                </button>
              </td>
            </tr>
          ))}
          {exclusions.length === 0 && (
            <tr><td className="table-cell text-gray-400" colSpan={5}>No exclusion configured — every discovered asset is analyzed</td></tr>
          )}
          <tr className="border-t border-gray-100 bg-gray-50/50">
            <td className="table-cell" colSpan={2}>
              <div className="flex items-center gap-2">
                <select value={scopeType} onChange={e => setScopeType(e.target.value as 'asset' | 'cidr')}
                  className="input-field py-0.5 px-1.5 text-xs">
                  <option value="cidr">IP range</option>
                  <option value="asset">Single asset</option>
                </select>
                {scopeType === 'cidr' ? (
                  <input value={cidr} onChange={e => setCidr(e.target.value)} placeholder="10.50.0.0/24"
                    className="input-field py-0.5 px-1.5 text-xs w-32 font-mono" />
                ) : (
                  <select value={assetId} onChange={e => setAssetId(e.target.value)} className="input-field py-0.5 px-1.5 text-xs w-48">
                    <option value="">Select an asset…</option>
                    {assets.map(a => <option key={a.id} value={a.id}>{a.hostname}{a.ip_address ? ` (${a.ip_address})` : ''}</option>)}
                  </select>
                )}
              </div>
            </td>
            <td className="table-cell">
              <select value={reason} onChange={e => setReason(e.target.value)} className="input-field py-0.5 px-1.5 text-xs">
                {EXCLUSION_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </td>
            <td className="table-cell">
              <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional note…"
                className="input-field py-0.5 px-1.5 text-xs w-32" />
            </td>
            <td className="table-cell">
              <button onClick={create} disabled={!canCreate || saving}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40">
                <Plus size={12} /> Add
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

interface NetworkSegment {
  id: number;
  tenant_id: number;
  tenant_name: string;
  name: string;
  cidr: string;
}

// Segmenti di rete "sensibili" per tenant: se almeno uno è configurato, il rogue-asset alert
// "New asset detected on network" scatta solo per gli asset con IP dentro uno di questi CIDR
// (vedi ingestion/assets.ts) invece che per ogni asset nuovo del tenant.
function NetworkSegmentsPanel({ segments, tenantId, onChanged }: { segments: NetworkSegment[]; tenantId: number | null; onChanged: () => void }) {
  const [name, setName] = useState('');
  const [cidr, setCidr] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function createSegment() {
    if (!tenantId || !name.trim() || !cidr.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await axios.post('/api/network-segments', { tenantId, name: name.trim(), cidr: cidr.trim() });
      setName(''); setCidr('');
      onChanged();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error while creating the segment');
    } finally {
      setSaving(false);
    }
  }

  async function deleteSegment(s: NetworkSegment) {
    if (!window.confirm(`Delete segment "${s.name}" (${s.cidr})?`)) return;
    await axios.delete(`/api/network-segments/${s.id}`);
    onChanged();
  }

  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-2.5 border-b border-gray-100">
        <h2 className="font-medium text-sm text-gray-900 inline-flex items-center gap-2"><Network size={15} /> Sensitive Network Segments</h2>
        <p className="text-xs text-gray-400">
          When a tenant has at least one segment configured, the "New asset detected" rogue-asset alert
          only fires for new assets whose IP falls inside one of these CIDR blocks — otherwise (no
          segments configured) it fires for any new asset, as before.
        </p>
      </div>
      {error && <div className="px-4 py-2 text-xs text-red-600 bg-red-50">{error}</div>}
      <table className="w-full text-sm">
        <thead><tr>
          <th className="table-header">Name</th><th className="table-header">Tenant</th>
          <th className="table-header">CIDR</th><th className="table-header"></th>
        </tr></thead>
        <tbody>
          {segments.map(s => (
            <tr key={s.id} className="border-t border-gray-100">
              <td className="table-cell font-medium text-gray-800">{s.name}</td>
              <td className="table-cell text-gray-500">{s.tenant_name}</td>
              <td className="table-cell font-mono text-xs text-gray-700">{s.cidr}</td>
              <td className="table-cell">
                <button onClick={() => deleteSegment(s)} className="p-1 text-gray-400 hover:text-red-600" title="Delete segment">
                  <Trash2 size={14} />
                </button>
              </td>
            </tr>
          ))}
          {segments.length === 0 && (
            <tr><td className="table-cell text-gray-400" colSpan={4}>No sensitive segment defined — rogue-asset alert applies to all new assets</td></tr>
          )}
          <tr className="border-t border-gray-100 bg-gray-50/50">
            <td className="table-cell">
              <input value={name} onChange={e => setName(e.target.value)} placeholder="Segment name…"
                className="input-field py-0.5 px-1.5 text-xs w-36" />
            </td>
            <td className="table-cell text-gray-400 text-xs">{tenantId ? '' : 'Select a tenant to create'}</td>
            <td className="table-cell">
              <input value={cidr} onChange={e => setCidr(e.target.value)} placeholder="10.20.0.0/24"
                className="input-field py-0.5 px-1.5 text-xs w-36 font-mono" />
            </td>
            <td className="table-cell">
              <button onClick={createSegment} disabled={!tenantId || !name.trim() || !cidr.trim() || saving}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40">
                <Plus size={12} /> Add
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

interface TagAlertThreshold {
  id: number;
  tenant_id: number;
  tenant_name: string;
  tag: string;
  min_severity: 'low' | 'medium' | 'high' | 'critical';
}

// Soglie di alert per tag: un alert su un asset con questo tag viene scartato se la sua severity
// è sotto la soglia configurata (vedi ingestion/detection.ts) — utile per silenziare il rumore di
// asset noti come rumorosi/a basso valore (es. tag "lab-test") senza toccare le regole globali.
function TagThresholdsPanel({ thresholds, tenantId, onChanged }: { thresholds: TagAlertThreshold[]; tenantId: number | null; onChanged: () => void }) {
  const [tag, setTag] = useState('');
  const [minSeverity, setMinSeverity] = useState<TagAlertThreshold['min_severity']>('medium');
  const [saving, setSaving] = useState(false);

  async function createThreshold() {
    if (!tenantId || !tag.trim()) return;
    setSaving(true);
    try {
      await axios.post('/api/tag-alert-thresholds', { tenantId, tag: tag.trim(), minSeverity });
      setTag(''); setMinSeverity('medium');
      onChanged();
    } finally {
      setSaving(false);
    }
  }

  async function deleteThreshold(t: TagAlertThreshold) {
    if (!window.confirm(`Delete the alert threshold for tag "${t.tag}"?`)) return;
    await axios.delete(`/api/tag-alert-thresholds/${t.id}`);
    onChanged();
  }

  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-2.5 border-b border-gray-100">
        <h2 className="font-medium text-sm text-gray-900 inline-flex items-center gap-2"><Filter size={15} /> Per-Tag Alert Thresholds</h2>
        <p className="text-xs text-gray-400">
          Alerts on assets carrying this tag are dropped if below the configured severity — an asset
          with multiple thresholding tags uses whichever is most permissive, so no single tag can
          hide a signal another tag considers worth alerting on.
        </p>
      </div>
      <table className="w-full text-sm">
        <thead><tr>
          <th className="table-header">Tag</th><th className="table-header">Tenant</th>
          <th className="table-header">Minimum severity</th><th className="table-header"></th>
        </tr></thead>
        <tbody>
          {thresholds.map(t => (
            <tr key={t.id} className="border-t border-gray-100">
              <td className="table-cell"><span className="badge bg-primary-50 text-primary-700">{t.tag}</span></td>
              <td className="table-cell text-gray-500">{t.tenant_name}</td>
              <td className="table-cell text-gray-700 capitalize">{t.min_severity}</td>
              <td className="table-cell">
                <button onClick={() => deleteThreshold(t)} className="p-1 text-gray-400 hover:text-red-600" title="Delete threshold">
                  <Trash2 size={14} />
                </button>
              </td>
            </tr>
          ))}
          {thresholds.length === 0 && (
            <tr><td className="table-cell text-gray-400" colSpan={4}>No tag threshold defined yet</td></tr>
          )}
          <tr className="border-t border-gray-100 bg-gray-50/50">
            <td className="table-cell">
              <input value={tag} onChange={e => setTag(e.target.value)} placeholder="Tag name…"
                className="input-field py-0.5 px-1.5 text-xs w-32" />
            </td>
            <td className="table-cell text-gray-400 text-xs">{tenantId ? '' : 'Select a tenant to create'}</td>
            <td className="table-cell">
              <select value={minSeverity} onChange={e => setMinSeverity(e.target.value as TagAlertThreshold['min_severity'])}
                className="input-field py-0.5 px-1.5 text-xs w-28">
                <option value="low">low</option><option value="medium">medium</option>
                <option value="high">high</option><option value="critical">critical</option>
              </select>
            </td>
            <td className="table-cell">
              <button onClick={createThreshold} disabled={!tenantId || !tag.trim() || saving}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40">
                <Plus size={12} /> Add
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// Gestione dei gruppi di asset: comportamento configurabile per gruppo (escalation di
// severità, soglia di irraggiungibilità, monitoraggio attivo). La creazione richiede un
// tenant selezionato perché i gruppi sono per-tenant.
interface NightAsset {
  id: number; hostname: string; ip_address: string | null; mac_address: string | null; business_critical: boolean;
  business_role: string | null; night_allowed: boolean; night_hours: number; nights: number; last_seen_at_night: string;
}
interface NightActivityData { timezone: string; dayStart: string; dayEnd: string; from: string; to: string; assets: NightAsset[] }

// Asset visti accesi di notte (2026-10-19, GET /api/assets/night-activity): fuori dalla fascia
// diurna del tenant (wizard di onboarding, step "Night-time"). Stessa lista della sezione
// "Assets active at night" del report periodico; l'export CSV dell'header la esporta come
// qualunque altra tabella della pagina.
function NightActivityPanel({ tenantId, onChanged }: { tenantId: number; onChanged: () => void }) {
  const [days, setDays] = useState(7);
  const [data, setData] = useState<NightActivityData | null>(null);
  const [loading, setLoading] = useState(false);
  const [hideAllowed, setHideAllowed] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const res = await axios.get('/api/assets/night-activity', { params: { tenantId, days } });
      setData(res.data.data);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, [tenantId, days]);

  async function authorise(a: NightAsset, allowed: boolean) {
    await axios.put(`/api/assets/${a.id}`, { nightAllowed: allowed });
    await load();
    onChanged();
  }

  const rows = (data?.assets || []).filter(a => !hideAllowed || !a.night_allowed);
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div>
          <h2 className="font-medium text-sm text-gray-900 inline-flex items-center gap-1.5"><Moon size={14} className="text-indigo-600" /> Assets active at night</h2>
          <p className="text-xs text-gray-400">
            {data ? `Night = outside ${data.dayStart}–${data.dayEnd} (${data.timezone}). ` : ''}
            Devices seen on the network, by the EDR or by the hypervisor during night hours. Change the window in the Onboarding wizard.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <label className="inline-flex items-center gap-1 text-gray-600">
            <input type="checkbox" checked={hideAllowed} onChange={e => setHideAllowed(e.target.checked)} /> Hide authorised
          </label>
          <select value={days} onChange={e => setDays(Number(e.target.value))} className="input-field py-0.5 px-1.5 text-xs w-32">
            <option value={1}>Last night</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
      </div>
      <table className="w-full text-sm" data-export-name="Assets active at night">
        <thead>
          <tr>
            <th className="table-header">Host</th><th className="table-header">IP</th><th className="table-header">Role</th>
            <th className="table-header">Nights</th><th className="table-header">Night hours</th><th className="table-header">Last seen at night</th>
            <th className="table-header">Night-time</th>
          </tr>
        </thead>
        <tbody>
          {loading && !data && <tr><td className="table-cell text-gray-400" colSpan={7}>Loading…</td></tr>}
          {rows.map(a => (
            <tr key={a.id} className={`border-t border-gray-100 ${a.night_allowed ? '' : 'bg-indigo-50/40'}`}>
              <td className="table-cell">
                <Link to={`/asset-center/${a.id}`} className="font-medium text-primary-600 hover:underline">{a.hostname}</Link>
                {a.business_critical && <Star size={11} fill="currentColor" className="inline ml-1 text-amber-500" />}
              </td>
              <td className="table-cell font-mono text-xs text-gray-500">{a.ip_address || '—'}</td>
              <td className="table-cell text-gray-600">{a.business_role || '—'}</td>
              <td className="table-cell">{a.nights}</td>
              <td className="table-cell">{a.night_hours}</td>
              <td className="table-cell text-gray-500">{new Date(a.last_seen_at_night).toLocaleString('en-GB', { timeZone: data?.timezone })}</td>
              <td className="table-cell">
                <button onClick={() => authorise(a, !a.night_allowed)}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${
                    a.night_allowed ? 'text-indigo-700 bg-indigo-50 hover:bg-indigo-100' : 'text-gray-500 bg-gray-50 hover:bg-gray-100'}`}>
                  <Moon size={12} fill={a.night_allowed ? 'currentColor' : 'none'} /> {a.night_allowed ? 'Authorised' : 'Authorise'}
                </button>
              </td>
            </tr>
          ))}
          {data && rows.length === 0 && (
            <tr><td className="table-cell text-gray-400" colSpan={7}>No asset seen active at night{hideAllowed ? ' without authorisation' : ''} in this period.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function GroupsPanel({ groups, tenantId, onChanged }: { groups: AssetGroup[]; tenantId: number | null; onChanged: () => void }) {
  const [name, setName] = useState('');
  const [boost, setBoost] = useState(0);
  const [minutes, setMinutes] = useState('');
  const [monitor, setMonitor] = useState(false);
  const [saving, setSaving] = useState(false);

  async function createGroup() {
    if (!tenantId || !name.trim()) return;
    setSaving(true);
    try {
      await axios.post('/api/asset-groups', {
        tenantId, name: name.trim(), severityBoost: boost,
        unreachableMinutes: minutes ? Number(minutes) : null, monitorReachability: monitor,
      });
      setName(''); setBoost(0); setMinutes(''); setMonitor(false);
      onChanged();
    } finally {
      setSaving(false);
    }
  }

  async function updateGroup(g: AssetGroup, patch: Record<string, unknown>) {
    await axios.put(`/api/asset-groups/${g.id}`, patch);
    onChanged();
  }

  async function deleteGroup(g: AssetGroup) {
    if (!window.confirm(`Delete group "${g.name}"? Its ${g.members_count} member assets will be ungrouped.`)) return;
    await axios.delete(`/api/asset-groups/${g.id}`);
    onChanged();
  }

  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-2.5 border-b border-gray-100">
        <h2 className="font-medium text-sm text-gray-900 inline-flex items-center gap-2"><Layers size={15} /> Asset Groups</h2>
        <p className="text-xs text-gray-400">Per-group behavior: alerts on member assets are escalated by the severity boost; the unreachable threshold overrides the 30-minute default; monitored groups get active reachability checks (ping from the broker) like Business Critical assets.</p>
      </div>
      <table className="w-full text-sm">
        <thead><tr>
          <th className="table-header">Name</th><th className="table-header">Tenant</th>
          <th className="table-header">Members</th><th className="table-header">Severity boost</th>
          <th className="table-header">Unreachable after (min)</th><th className="table-header">Active monitoring</th>
          <th className="table-header"></th>
        </tr></thead>
        <tbody>
          {groups.map(g => (
            <tr key={g.id} className="border-t border-gray-100">
              <td className="table-cell font-medium text-gray-800">{g.name}</td>
              <td className="table-cell text-gray-500">{g.tenant_name}</td>
              <td className="table-cell text-gray-500">{g.members_count}</td>
              <td className="table-cell">
                <select value={g.severity_boost} onChange={e => updateGroup(g, { severityBoost: Number(e.target.value) })}
                  className="input-field py-0.5 px-1.5 text-xs w-24">
                  <option value={0}>None</option><option value={1}>+1 level</option><option value={2}>+2 levels</option>
                </select>
              </td>
              <td className="table-cell">
                <EditableText value={g.unreachable_minutes !== null ? String(g.unreachable_minutes) : null}
                  placeholder="30 (default)"
                  onSave={v => updateGroup(g, { unreachableMinutes: v ? Number(v) : null })} />
              </td>
              <td className="table-cell">
                <input type="checkbox" checked={g.monitor_reachability}
                  onChange={e => updateGroup(g, { monitorReachability: e.target.checked })} />
              </td>
              <td className="table-cell">
                <button onClick={() => deleteGroup(g)} className="p-1 text-gray-400 hover:text-red-600" title="Delete group">
                  <Trash2 size={14} />
                </button>
              </td>
            </tr>
          ))}
          {groups.length === 0 && (
            <tr><td className="table-cell text-gray-400" colSpan={7}>No group defined yet</td></tr>
          )}
          <tr className="border-t border-gray-100 bg-gray-50/50">
            <td className="table-cell">
              <input value={name} onChange={e => setName(e.target.value)} placeholder="New group name…"
                className="input-field py-0.5 px-1.5 text-xs w-36" />
            </td>
            <td className="table-cell text-gray-400 text-xs">{tenantId ? '' : 'Select a tenant to create'}</td>
            <td className="table-cell"></td>
            <td className="table-cell">
              <select value={boost} onChange={e => setBoost(Number(e.target.value))} className="input-field py-0.5 px-1.5 text-xs w-24">
                <option value={0}>None</option><option value={1}>+1 level</option><option value={2}>+2 levels</option>
              </select>
            </td>
            <td className="table-cell">
              <input value={minutes} onChange={e => setMinutes(e.target.value.replace(/\D/g, ''))} placeholder="30 (default)"
                className="input-field py-0.5 px-1.5 text-xs w-24" />
            </td>
            <td className="table-cell"><input type="checkbox" checked={monitor} onChange={e => setMonitor(e.target.checked)} /></td>
            <td className="table-cell">
              <button onClick={createGroup} disabled={!tenantId || !name.trim() || saving}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40">
                <Plus size={12} /> Add
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// Inventario degli asset visti negli eventi di sicurezza, identificati primariamente per MAC
// address (stabile nel tempo a differenza di hostname/IP). Divisi in tre gruppi: Business
// Critical (monitorati per raggiungibilità, vedi jobs/assetMonitor.ts sul backend), rilevati
// abitualmente e rilevati occasionalmente.
export default function AssetsPage() {
  const { selectedTenantId, selectedTenantName } = useTenantScope();
  const { orderedDefs, visible, setVisible: setVisibleColumns, moveColumn, reset: resetColumns } = useColumnPrefs('soc.columns.assets', ASSET_COLUMNS);
  const shownColumns = orderedDefs.filter(d => visible.has(d.key));
  const [assets, setAssets] = useState<Asset[]>([]);
  const [groups, setGroups] = useState<AssetGroup[]>([]);
  const [showGroupsPanel, setShowGroupsPanel] = useState(false);
  const [segments, setSegments] = useState<NetworkSegment[]>([]);
  const [showSegmentsPanel, setShowSegmentsPanel] = useState(false);
  const [thresholds, setThresholds] = useState<TagAlertThreshold[]>([]);
  const [showThresholdsPanel, setShowThresholdsPanel] = useState(false);
  const [exclusions, setExclusions] = useState<AnalysisExclusion[]>([]);
  const [showExclusionsPanel, setShowExclusionsPanel] = useState(false);
  const [showNightPanel, setShowNightPanel] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tagDraft, setTagDraft] = useState<Record<number, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [edrFilter, setEdrFilter] = useState<EdrFilter>('all');
  const [remoteOnly, setRemoteOnly] = useState(false);
  const [onlyGroup, setOnlyGroup] = useState<'' | 'critical' | 'habitual' | 'occasional'>('');
  const [search, setSearch] = useState('');

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const params = { params: { tenantId: selectedTenantId ?? undefined } };
      const [assetsRes, groupsRes, segmentsRes, thresholdsRes, exclusionsRes] = await Promise.all([
        axios.get('/api/assets', params),
        axios.get('/api/asset-groups', params),
        axios.get('/api/network-segments', params),
        axios.get('/api/tag-alert-thresholds', params),
        axios.get('/api/analysis-exclusions', params),
      ]);
      setAssets(assetsRes.data.data);
      setGroups(groupsRes.data.data);
      setSegments(segmentsRes.data.data);
      setThresholds(thresholdsRes.data.data);
      setExclusions(exclusionsRes.data.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error while loading the asset inventory');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [selectedTenantId]);

  async function toggleCritical(a: Asset) {
    await axios.put(`/api/assets/${a.id}`, { businessCritical: !a.business_critical });
    load();
  }

  async function addTag(a: Asset) {
    const value = (tagDraft[a.id] || '').trim();
    if (!value || a.tags.includes(value)) return;
    await axios.put(`/api/assets/${a.id}`, { tags: [...a.tags, value] });
    setTagDraft(prev => ({ ...prev, [a.id]: '' }));
    load();
  }

  async function removeTag(a: Asset, tag: string) {
    await axios.put(`/api/assets/${a.id}`, { tags: a.tags.filter(t => t !== tag) });
    load();
  }

  async function saveField(a: Asset, field: 'owner' | 'notes' | 'businessRole', value: string) {
    await axios.put(`/api/assets/${a.id}`, { [field]: value });
    load();
  }

  async function toggleNight(a: Asset) {
    await axios.put(`/api/assets/${a.id}`, { nightAllowed: !a.night_allowed });
    load();
  }

  async function saveTcpPort(a: Asset, value: string) {
    const trimmed = value.trim();
    await axios.put(`/api/assets/${a.id}`, { checkTcpPort: trimmed === '' ? null : Number(trimmed) });
    load();
  }

  async function assignGroup(a: Asset, groupId: number | null) {
    await axios.put(`/api/assets/${a.id}`, { groupId });
    load();
  }

  function renderCell(a: Asset, key: string) {
    switch (key) {
      case 'mac':
        return a.mac_address
          ? <span className="font-mono text-xs font-semibold text-gray-900">{a.mac_address}</span>
          : <span className="text-gray-300">unknown</span>;
      case 'hostname':
        return (
          <Link to={`/asset-center/${a.id}`} className="font-medium text-primary-600 hover:underline">
            {a.hostname}
          </Link>
        );
      case 'ip':
        return <span className="font-mono text-xs text-gray-500">{a.ip_address || '—'}</span>;
      case 'vendor':
        return a.vendor
          ? <span className="text-gray-700">{a.vendor}</span>
          : <span className="text-gray-300">—</span>;
      case 'type':
        return a.device_type
          ? <span className="badge bg-sky-50 text-sky-700">{DEVICE_TYPE_LABELS[a.device_type] || a.device_type}</span>
          : <span className="text-gray-300">—</span>;
      case 'edr': {
        const state = edrState(a);
        if (state === 'n/a') return <span className="text-gray-300">—</span>;
        if (state === 'unprotected') {
          return (
            <span className="inline-flex items-center gap-1 badge bg-amber-100 text-amber-700"
              title={`Seen on the network but not reported by any EDR of this tenant. Counted as a gap because ${EDR_REASON_LABELS[a.edr_state_reason] || 'it can run an agent'}.`}>
              <ShieldOff size={11} /> Not protected
            </span>
          );
        }
        if (state === 'ineligible') {
          return (
            <span className="inline-flex items-center gap-1 badge bg-gray-100 text-gray-600"
              title={`Cannot run an EDR agent (${EDR_REASON_LABELS[a.edr_state_reason]}${a.edr_state_reason === 'oui' && a.vendor ? `: ${a.vendor}` : ''}). Tag it "edr-required" to count it as a gap anyway.`}>
              Not eligible
            </span>
          );
        }
        if (state === 'unclassified') {
          return (
            <span className="inline-flex items-center gap-1 badge bg-gray-50 text-gray-400 border border-dashed border-gray-300"
              title={'No machine name, operating system or known card vendor: protection can be neither confirmed nor excluded. Tag it "no-edr" or "edr-required" once identified.'}>
              Unidentified
            </span>
          );
        }
        const vendors = (a.edr_vendors || []).map(v => EDR_LABELS[v] || v).join(', ');
        const danger = a.edr_infected || a.edr_isolated;
        return (
          <span className="inline-flex items-center gap-1 flex-wrap">
            <span className={`inline-flex items-center gap-1 badge ${danger ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}
              title={`Protected by ${vendors}`}>
              {danger ? <ShieldAlert size={11} /> : <ShieldCheck size={11} />} {vendors}
            </span>
            {a.edr_online === false && <span className="badge bg-gray-100 text-gray-500" title="Agent offline according to the EDR">offline</span>}
            {a.edr_isolated && <span className="badge bg-red-50 text-red-700" title="Network isolated by the EDR">isolated</span>}
            {a.edr_infected && <span className="badge bg-red-50 text-red-700" title="Active threats reported by the EDR">infected</span>}
            {a.edr_up_to_date === false && <span className="badge bg-amber-50 text-amber-700" title="Agent not up to date">outdated agent</span>}
          </span>
        );
      }
      case 'owner':
        return <EditableText value={a.owner} placeholder="Add owner…" onSave={v => saveField(a, 'owner', v)} />;
      case 'tcp_port':
        return (
          <EditableText value={a.check_tcp_port ? String(a.check_tcp_port) : null}
            placeholder="e.g. 443" onSave={v => saveTcpPort(a, v)} />
        );
      case 'notes':
        return <EditableText value={a.notes} placeholder="Add note…" onSave={v => saveField(a, 'notes', v)} />;
      case 'role':
        return <EditableText value={a.business_role} placeholder="e.g. ERP server…" onSave={v => saveField(a, 'businessRole', v)} />;
      case 'night':
        return (
          <button onClick={() => toggleNight(a)}
            title={a.night_allowed ? 'Authorised to stay on at night (click to revoke)' : 'Expected to be off at night (click to authorise)'}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${
              a.night_allowed ? 'text-indigo-700 bg-indigo-50 hover:bg-indigo-100' : 'text-gray-400 bg-gray-50 hover:bg-gray-100'}`}>
            <Moon size={12} fill={a.night_allowed ? 'currentColor' : 'none'} /> {a.night_allowed ? 'Allowed at night' : 'Off at night'}
          </button>
        );
      case 'group': {
        const tenantGroups = groups.filter(g => g.tenant_id === a.tenant_id);
        return (
          <select
            value={a.group_id ?? ''}
            onChange={e => assignGroup(a, e.target.value ? Number(e.target.value) : null)}
            className="input-field py-0.5 px-1.5 text-xs w-32"
            style={a.group_color ? { borderColor: a.group_color } : undefined}>
            <option value="">No group</option>
            {tenantGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        );
      }
      case 'tags':
        return (
          <div className="flex flex-wrap items-center gap-1 max-w-xs">
            {a.tags.map(tag => (
              <span key={tag} className="badge bg-primary-50 text-primary-700 inline-flex items-center gap-1">
                <Tag size={10} /> {tag}
                <button onClick={() => removeTag(a, tag)} className="hover:text-red-600" title="Remove tag">
                  <X size={10} />
                </button>
              </span>
            ))}
            <input
              value={tagDraft[a.id] || ''}
              onChange={e => setTagDraft(prev => ({ ...prev, [a.id]: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') addTag(a); }}
              placeholder="Add tag…"
              className="input-field py-0.5 px-1.5 text-xs w-20"
            />
            <button onClick={() => addTag(a)} className="p-0.5 text-gray-400 hover:text-primary-600" title="Add tag">
              <Plus size={12} />
            </button>
          </div>
        );
      case 'detection':
        return a.frequency === 'habitual'
          ? <span className="badge bg-emerald-100 text-emerald-700">Habitual</span>
          : <span className="badge bg-gray-100 text-gray-600">Occasional</span>;
      case 'risk_score':
        return (
          <Link to={`/asset-center/${a.id}`} title="Based on recent alerts, weighted by severity and recency"
            className={`badge font-semibold ${riskScoreStyle(a.risk_score)}`}>
            {a.risk_score}
          </Link>
        );
      case 'status': {
        // L'esito del check attivo (ping dal broker) è autorevole se recente (15 min).
        const freshCheck = a.active_check_at
          && Date.now() - new Date(a.active_check_at).getTime() < 15 * 60 * 1000;
        const pingBadge = freshCheck && (
          a.active_check_ok
            ? <span className="inline-flex items-center gap-1 badge bg-emerald-50 text-emerald-700" title="Active check (ping from broker)">
                <Activity size={10} /> Ping{a.active_check_rtt_ms !== null ? ` ${a.active_check_rtt_ms}ms` : ' OK'}
              </span>
            : <span className="inline-flex items-center gap-1 badge bg-red-50 text-red-700" title="Active check (ping from broker)">
                <Activity size={10} /> Ping failed
              </span>
        );
        // Distinto dal ping: un host può rispondere al ping con il servizio applicativo giù.
        const tcpBadge = freshCheck && a.check_tcp_port && (
          a.active_check_tcp_ok
            ? <span className="inline-flex items-center gap-1 badge bg-emerald-50 text-emerald-700" title={`TCP check on port ${a.check_tcp_port}`}>
                <Activity size={10} /> Port {a.check_tcp_port} OK
              </span>
            : <span className="inline-flex items-center gap-1 badge bg-red-50 text-red-700" title={`TCP check on port ${a.check_tcp_port}`}>
                <Activity size={10} /> Port {a.check_tcp_port} failed
              </span>
        );
        return (
          <span className="inline-flex items-center gap-1.5">
            {a.reachable
              ? <span className="inline-flex items-center gap-1.5 badge bg-green-100 text-green-700"><Wifi size={11} /> Reachable</span>
              : <span className="inline-flex items-center gap-1.5 badge bg-red-100 text-red-700"><WifiOff size={11} /> Unreachable · {formatAgo(a.silent_minutes)}</span>}
            {pingBadge}
            {tcpBadge}
          </span>
        );
      }
      case 'last_seen':
        return <span className="text-gray-500" title={new Date(a.last_seen_at).toLocaleString('en-GB')}>{formatAgo(a.silent_minutes)}</span>;
      case 'first_seen':
        return <span className="text-gray-500">{new Date(a.first_seen_at).toLocaleDateString('en-GB')}</span>;
      case 'critical':
        return (
          <button onClick={() => toggleCritical(a)}
            title={a.business_critical ? 'Remove from Business Critical' : 'Mark as Business Critical'}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${
              a.business_critical ? 'text-amber-700 bg-amber-50 hover:bg-amber-100' : 'text-gray-400 bg-gray-50 hover:bg-gray-100'}`}>
            <Star size={13} fill={a.business_critical ? 'currentColor' : 'none'} /> {a.business_critical ? 'Critical' : 'Mark critical'}
          </button>
        );
      default:
        return null;
    }
  }

  function Group({ id, title, description, rows, highlight }: { id: string; title: string; description: string; rows: Asset[]; highlight?: boolean }) {
    return (
      <div id={id} className="card overflow-hidden scroll-mt-20">
        <div className="px-4 py-2.5 border-b border-gray-100">
          <h2 className={`font-medium text-sm ${highlight ? 'text-amber-700' : 'text-gray-900'}`}>{title}</h2>
          <p className="text-xs text-gray-400">{description}</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                {shownColumns.map(d => (
                  <DraggableTh key={d.key} colKey={d.key} onMove={moveColumn} className="table-header whitespace-nowrap">
                    {d.label}
                  </DraggableTh>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(a => (
                <tr key={a.id} className={`border-t border-gray-100 hover:bg-gray-50 ${highlight ? 'bg-amber-50/50' : ''}`}>
                  {shownColumns.map(d => <td key={d.key} className="table-cell">{renderCell(a, d.key)}</td>)}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td className="table-cell text-gray-400" colSpan={shownColumns.length}>No asset in this group</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  const unprotectedCount = assets.filter(a => edrState(a) === 'unprotected').length;
  const protectedCount = assets.filter(a => edrState(a) === 'protected').length;
  const ineligibleCount = assets.filter(a => edrState(a) === 'ineligible').length;
  const unclassifiedCount = assets.filter(a => edrState(a) === 'unclassified').length;
  const coveragePct = protectedCount + unprotectedCount > 0 ? Math.round((protectedCount / (protectedCount + unprotectedCount)) * 100) : null;
  const pendingRemoteCount = assets.filter(remotePending).length;
  // Ricerca testuale sui campi che un analista cerca davvero (nome, IP, MAC, vendor, tipo,
  // owner, note, tag, gruppo, EDR, tenant): ogni parola deve comparire in almeno un campo.
  const terms = search.toLowerCase().split(/\s+/).filter(Boolean);
  const matchesSearch = (a: Asset) => {
    if (terms.length === 0) return true;
    const hay = [a.hostname, a.ip_address, a.mac_address, a.vendor, a.device_type, a.owner, a.notes, a.business_role,
      a.group_name, a.tenant_name, ...(a.tags || []), ...(a.edr_vendors || [])].filter(Boolean).join(' ').toLowerCase();
    return terms.every(t => hay.includes(t));
  };
  const scoped = (edrFilter === 'all' ? assets : assets.filter(a => edrState(a) === edrFilter))
    .filter(a => !remoteOnly || remotePending(a))
    .filter(matchesSearch);
  const critical = scoped.filter(a => a.business_critical);
  const habitual = scoped.filter(a => !a.business_critical && a.frequency === 'habitual');
  const occasional = scoped.filter(a => !a.business_critical && a.frequency === 'occasional');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-1">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-xl font-semibold text-gray-900">Asset Inventory</h1>
          <TenantSelect />
        </div>
        <div className="flex items-center gap-2">
          {pendingRemoteCount > 0 && (
            <button onClick={() => setRemoteOnly(v => !v)}
              title="Assets where a remote access tool was observed but no policy is set (tenant in informational mode). Open the asset to set its policy."
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border ${
                remoteOnly ? 'bg-amber-100 text-amber-800 border-amber-300' : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'}`}>
              <ScreenShare size={13} /> Remote tools seen, no policy: {pendingRemoteCount}
            </button>
          )}
          <button onClick={() => setShowGroupsPanel(v => !v)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border ${
              showGroupsPanel ? 'bg-primary-50 text-primary-700 border-primary-200' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
            <Layers size={13} /> Groups
          </button>
          <button onClick={() => setShowSegmentsPanel(v => !v)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border ${
              showSegmentsPanel ? 'bg-primary-50 text-primary-700 border-primary-200' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
            <Network size={13} /> Segments
          </button>
          <button onClick={() => setShowThresholdsPanel(v => !v)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border ${
              showThresholdsPanel ? 'bg-primary-50 text-primary-700 border-primary-200' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
            <Filter size={13} /> Tag Thresholds
          </button>
          <button onClick={() => setShowExclusionsPanel(v => !v)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border ${
              showExclusionsPanel ? 'bg-primary-50 text-primary-700 border-primary-200' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
            <EyeOff size={13} /> Exclusions
          </button>
          {selectedTenantId && (
            <button onClick={() => setShowNightPanel(v => !v)}
              title="Assets seen active outside the tenant's daytime window"
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border ${
                showNightPanel ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
              <Moon size={13} /> Night activity
            </button>
          )}
          <ColumnPicker defs={orderedDefs} visible={visible} onChange={setVisibleColumns} onReset={resetColumns} onMove={moveColumn} />
        </div>
      </div>

      {showGroupsPanel && (
        <GroupsPanel groups={groups} tenantId={selectedTenantId} onChanged={load} />
      )}
      {showSegmentsPanel && (
        <NetworkSegmentsPanel segments={segments} tenantId={selectedTenantId} onChanged={load} />
      )}
      {showThresholdsPanel && (
        <TagThresholdsPanel thresholds={thresholds} tenantId={selectedTenantId} onChanged={load} />
      )}
      {showExclusionsPanel && (
        <ExclusionsPanel exclusions={exclusions} assets={assets} tenantId={selectedTenantId} onChanged={load} />
      )}
      {showNightPanel && selectedTenantId && (
        <NightActivityPanel tenantId={selectedTenantId} onChanged={load} />
      )}

      {!loading && (protectedCount > 0 || unprotectedCount > 0 || ineligibleCount > 0 || unclassifiedCount > 0) && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-gray-500">EDR coverage{coveragePct !== null ? ` ${coveragePct}%` : ''}:</span>
          {([
            ['all', `All (${assets.length})`],
            ['protected', `Protected (${protectedCount})`],
            ['unprotected', `Not protected (${unprotectedCount})`],
            ['ineligible', `Not eligible (${ineligibleCount})`],
            ['unclassified', `Unidentified (${unclassifiedCount})`],
          ] as [EdrFilter, string][]).map(([key, label]) => (
            <button key={key} onClick={() => setEdrFilter(key)}
              title={key === 'protected' ? 'Reported by an EDR agent that is still active'
                : key === 'unprotected' ? 'Can run an agent (machine name, known OS or PC/server/VM card vendor) but no EDR reports it: the real gap'
                : key === 'ineligible' ? 'Printers, network equipment, cameras (card vendor) or tagged no-edr: cannot run an agent'
                : key === 'unclassified' ? 'No name, OS or known vendor: protection can be neither confirmed nor excluded'
                : undefined}
              className={`px-2 py-1 rounded-md border ${edrFilter === key
                ? (key === 'unprotected' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-primary-50 text-primary-700 border-primary-200')
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
              {label}
            </button>
          ))}
          <span className="text-gray-400 ml-1">Coverage = protected / (protected + not protected). Tags: <code>no-edr</code> marks a device as not eligible, <code>edr-required</code> forces it into the gap list.</span>
        </div>
      )}

      {loading && assets.length === 0 && <div className="card p-4 text-gray-400 text-sm">Loading…</div>}

      {error && <div className="card p-4 text-sm text-red-600 bg-red-50">{error}</div>}

      {!loading && !error && assets.length === 0 && (
        <div className="card p-8 text-center text-sm text-gray-500">No asset detected yet on the current scope.</div>
      )}

      {!loading && assets.length > 0 && (
        <>
          {/* Link diretti alle tre sezioni (richiesta dell'utente): con centinaia di asset la
              sezione cercata sta molto in basso. Scorrono alla sezione; con "Show only" la
              pagina mostra solo quella. */}
          <div className="flex items-center gap-2 text-xs flex-wrap">
            <div className="relative">
              <Search size={13} className="absolute left-2 top-1.5 text-gray-400" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search host, IP, MAC, vendor, owner, tag…"
                className="input-field py-0.5 pl-7 pr-6 text-xs w-72" aria-label="Search assets" />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-1.5 top-1 text-gray-400 hover:text-gray-600" aria-label="Clear search"><X size={13} /></button>
              )}
            </div>
            {terms.length > 0 && <span className="text-gray-500">{scoped.length} of {assets.length}</span>}
            <span className="text-gray-300">|</span>
            <span className="text-gray-500">Jump to:</span>
            {([['critical', 'Business Critical', critical.length, 'text-amber-700 bg-amber-50 border-amber-200 hover:bg-amber-100'],
              ['habitual', 'Detected habitually', habitual.length, 'text-gray-700 bg-white border-gray-200 hover:bg-gray-50'],
              ['occasional', 'Detected occasionally', occasional.length, 'text-gray-700 bg-white border-gray-200 hover:bg-gray-50']] as [string, string, number, string][]).map(([key, label, count, cls]) => (
              <a key={key} href={`#assets-${key}`}
                onClick={e => { e.preventDefault(); document.getElementById(`assets-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                className={`px-2 py-1 rounded-md border ${cls} ${onlyGroup === key ? 'ring-2 ring-primary-300' : ''}`}>
                {label} <span className="font-semibold">{count}</span>
              </a>
            ))}
            <span className="text-gray-300">|</span>
            <span className="text-gray-500">Show only:</span>
            <select value={onlyGroup} onChange={e => setOnlyGroup(e.target.value as typeof onlyGroup)} className="input-field py-0.5 px-1.5 text-xs w-44">
              <option value="">All sections</option>
              <option value="critical">Business Critical</option>
              <option value="habitual">Detected habitually</option>
              <option value="occasional">Detected occasionally</option>
            </select>
          </div>
          {(!onlyGroup || onlyGroup === 'critical') && (
            <Group id="assets-critical" title="Business Critical" highlight
              description="Manually marked assets, monitored for reachability: an alert/incident is raised when one goes silent."
              rows={critical} />
          )}
          {(!onlyGroup || onlyGroup === 'habitual') && (
            <Group id="assets-habitual" title="Detected habitually"
              description="Assets seen regularly over the last 14 days."
              rows={habitual} />
          )}
          {(!onlyGroup || onlyGroup === 'occasional') && (
            <Group id="assets-occasional" title="Detected occasionally"
              description="Assets seen only sporadically over the last 14 days."
              rows={occasional} />
          )}
        </>
      )}
    </div>
  );
}
